import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import { createCartesianViewport, createFormulaLegend } from '@learnforge/demo-render';
import { parse, evaluate } from '@learnforge/demo-math/expr';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { createRiemannBars, type RiemannBarsHandle } from '../render/riemann-bars';
import { compileFn, sampleFn, riemannSum, riemannRects } from '../calc';
import type { RiemannRule } from '../calc/riemann';
import { INTEGRAL_PRESETS, type IntegralPreset } from '../data/integral-presets';
import { createCartesianPlane, type CartesianPlaneHandle } from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

/** Stage dimensions in pixels. */
const STAGE_W = 800;
const STAGE_H = 460;
const PLOT_RECT = { x: 0, y: 0, width: STAGE_W, height: STAGE_H };
/** Number of sample points for the function curve. */
const SAMPLE_N = 400;

// ─── State ────────────────────────────────────────────────────────────────────

export interface RiemannLabState {
  presetId: string;
  n: number;
  rule: RiemannRule;
}

// ─── Pure helpers (exported and tested) ──────────────────────────────────────

/**
 * Compute the exact definite integral ∫ₐᵇ f(x) dx from the curated antiderivative.
 *
 * Uses `evaluate(parse(F).node, { x: b }) - evaluate(parse(F).node, { x: a })`.
 */
function exactIntegral(preset: IntegralPreset): number {
  const FNode = parse(preset.F).node;
  return evaluate(FNode, { x: preset.b }) - evaluate(FNode, { x: preset.a });
}

/**
 * Build the three readout rows for the current riemann-lab state:
 *  - `riemann-sum`    : Σ (the Riemann sum approximation)
 *  - `exact-integral` : ∫ (from the curated antiderivative)
 *  - `riemann-error`  : |Σ − ∫|
 */
export function riemannReadoutModel(state: RiemannLabState): ReadoutRow[] {
  const preset = INTEGRAL_PRESETS.find((p) => p.id === state.presetId) ?? INTEGRAL_PRESETS[0]!;
  const f = compileFn(preset.f);
  const sigma = riemannSum(f, preset.a, preset.b, state.n, state.rule);
  const exact = exactIntegral(preset);
  const error = Math.abs(sigma - exact);

  return [
    {
      id: 'riemann-sum',
      lhs: 'Σ',
      relation: '≈',
      rhs: formatNumber(sigma, { decimals: 4 }),
    },
    {
      id: 'exact-integral',
      lhs: '∫',
      relation: '=',
      rhs: formatNumber(exact, { decimals: 4 }),
    },
    {
      id: 'riemann-error',
      lhs: '|Σ − ∫|',
      relation: '=',
      rhs: formatNumber(error, { decimals: 4 }),
      emphasis: error < 0.01 ? ('invariant' as const) : ('neutral' as const),
    },
  ];
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<RiemannLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let plane: CartesianPlaneHandle;
  let curves: CurveLayerHandle;
  let bars: RiemannBarsHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let legend: FormulaLegendHandle;
  let activePresetId = INTEGRAL_PRESETS[0]!.id;

  function fittedBase(preset: IntegralPreset) {
    const f = compileFn(preset.f);
    const xDomain: [number, number] = preset.fittedXDomain
      ? [...preset.fittedXDomain]
      : [preset.a - 1, preset.b + 1];
    const points = sampleFn(f, xDomain[0], xDomain[1], SAMPLE_N);
    const values = points.map((point) => point.y).filter(Number.isFinite);
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const span = max - min;
    const pad = Math.max(0.5, span * 0.15);
    return { xDomain, yDomain: [min - pad, max + pad] as [number, number] };
  }

  function defaultState(_ctx: CalculusContext): RiemannLabState {
    return {
      presetId: INTEGRAL_PRESETS[0]!.id,
      n: 8,
      rule: 'left',
    };
  }

  function applyPresetLocal(state: RiemannLabState, id: string): RiemannLabState {
    const preset = INTEGRAL_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    return { ...state, presetId: preset.id };
  }

  function getPreset(state: RiemannLabState): IntegralPreset {
    return INTEGRAL_PRESETS.find((p) => p.id === state.presetId) ?? INTEGRAL_PRESETS[0]!;
  }

  return {
    labelKey: 'experiment.riemann-lab',
    demoId: 'calculus',
    advancedKey: 'riemann-lab',

    initialState(ctx): RiemannLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): RiemannLabState {
      return applyPresetLocal(state, id);
    },

    setup(_ctx: CalculusContext, controller: HarnessController<RiemannLabState>): void {
      const base = fittedBase(INTEGRAL_PRESETS[0]!);
      plane = createCartesianPlane(controller.svg, { domains: base, plotRect: PLOT_RECT });
      controller.registerCleanup(() => plane.destroy());

      bars = createRiemannBars(controller.svg, plane);
      controller.registerCleanup(() => bars.destroy());

      curves = createCurveLayer(controller.svg, plane);
      controller.registerCleanup(() => curves.destroy());

      readout = controller.createReadout();
      legend = createFormulaLegend(_ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base,
        plotRect: PLOT_RECT,
        onChange(next) {
          plane.setDomains(next);
          controller.requestRender();
        },
      });
      controller.registerCleanup(() => viewport.dispose());
    },

    render(state: RiemannLabState, _ctx: CalculusContext): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        const base = fittedBase(preset);
        activePresetId = preset.id;
        viewport.setBase(base, { notify: false });
        plane.setDomains(base);
      }
      const f = compileFn(preset.f);

      // ── Function curve ──
      const domains = plane.getDomains();
      curves.setPolyline(
        'f',
        sampleFn(f, domains.xDomain[0], domains.xDomain[1], SAMPLE_N),
        'calc-curve-primary',
        preset.discontinuities,
      );

      // ── Riemann bars ──
      const rects = riemannRects(f, preset.a, preset.b, state.n, state.rule);
      bars.setBars(rects, 0, 'calc-bar');
      legend.setEntries([{ id: 'f', formula: `f(x) = ${preset.f}`, color: 'var(--accent)' }]);

      // ── Readout ──
      readout.update(riemannReadoutModel(state));
    },

    onDrag(state: RiemannLabState): RiemannLabState {
      return state;
    },

    panel(state: RiemannLabState, ctx): PanelRow<CalcKey, RiemannLabState>[] {
      const rows: PanelRow<CalcKey, RiemannLabState>[] = [];

      // Preset dropdown
      rows.push({
        kind: 'dropdown',
        id: 'riemann-preset',
        labelKey: 'riemann.panel.preset',
        options: INTEGRAL_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // n slider (1–200)
      rows.push({
        kind: 'slider',
        id: 'riemann-n',
        labelKey: 'riemann.panel.n',
        min: 1,
        max: 200,
        step: 1,
        value: state.n,
        format: (v) => String(Math.round(v)),
        onChange: (v) => ({ ...state, n: Math.max(1, Math.round(v)) }),
      });

      // Rule dropdown — B5 lesson: mid/trap gated by advanced.
      // Always include the currently-selected rule so it never renders blank.
      const basicRules: { value: RiemannRule; labelKey: CalcKey }[] = [
        { value: 'left', labelKey: 'riemann.rule.left' },
        { value: 'right', labelKey: 'riemann.rule.right' },
      ];
      const advancedRules: { value: RiemannRule; labelKey: CalcKey }[] = [
        { value: 'mid', labelKey: 'riemann.rule.mid' },
        { value: 'trap', labelKey: 'riemann.rule.trap' },
      ];

      // Build the visible options:
      // - If advanced: show all four.
      // - If NOT advanced: show left+right, plus the current rule if it's mid/trap
      //   (so the dropdown never renders blank when rule is already mid/trap).
      let ruleOptions: { value: RiemannRule; labelKey: CalcKey }[];
      if (ctx.advanced) {
        ruleOptions = [...basicRules, ...advancedRules];
      } else {
        const currentIsAdvanced = state.rule === 'mid' || state.rule === 'trap';
        if (currentIsAdvanced) {
          const currentEntry = advancedRules.find((r) => r.value === state.rule)!;
          ruleOptions = [...basicRules, currentEntry];
        } else {
          ruleOptions = basicRules;
        }
      }

      rows.push({
        kind: 'dropdown',
        id: 'riemann-rule',
        labelKey: 'riemann.panel.rule',
        options: ruleOptions,
        value: state.rule,
        onChange: (v) => ({ ...state, rule: v as RiemannRule }),
      });

      // Advanced toggle
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (_state: RiemannLabState, ctx): ActionBarSpec<CalcKey, RiemannLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * INTEGRAL_PRESETS.length);
          const preset = INTEGRAL_PRESETS[idx]!;
          return { ...s, presetId: preset.id };
        },
        presets: INTEGRAL_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: RiemannLabState, _shotId: string): RiemannLabState {
      return {
        ...state,
        n: 8,
        rule: 'left',
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
